import { useEffect, useRef, useState } from 'react';
import { UseFormGetValues, UseFormSetValue, UseFormSubscribe } from 'react-hook-form';
import { TimecardDto } from 'types/timecards';

export type DescriptionAiEdit = {
    /** Whether the description holds a text the AI wrote, until that text is next edited. */
    isAiText: boolean;
    /** Writes an AI text into the description, unsaved; `replaced` is the text it takes the place of. */
    apply: (text: string, replaced: string) => void;
    /** Puts back the user's own text, the one the first of the AI edits in a row replaced. */
    undo: () => void;
};

type Props = {
    getValues: UseFormGetValues<TimecardDto>;
    setValue: UseFormSetValue<TimecardDto>;
    subscribe: UseFormSubscribe<TimecardDto>;
};

/**
 * The AI edits of a timecard description: an edit replaces the text, unsaved, and Undo puts back the user's own
 * text however many AI edits followed one another, until the user edits the text themselves.
 */
export function useDescriptionAiEdit({ getValues, setValue, subscribe }: Props): DescriptionAiEdit {
    const [original, setOriginal] = useState<string | null>(null);
    const written = useRef<string | null>(null);

    useEffect(
        () =>
            subscribe({
                name: 'description',
                formState: { values: true },
                callback: () => {
                    // Compared rather than dropped on any change: the trim on blur is a change that edits nothing.
                    if (getValues('description') !== written.current) {
                        written.current = null;
                        setOriginal(null);
                    }
                },
            }),
        [getValues, subscribe]
    );

    const apply = (aiText: string, replaced: string) => {
        // Trimmed as the field trims on blur, which would otherwise read as an edit and drop the Undo.
        const text = aiText.trim();
        // Set before the write, so the change it fires reads as the AI's own and keeps the Undo.
        written.current = text;
        setValue('description', text, { shouldDirty: true });
        setOriginal((previous) => previous ?? replaced);
    };

    const undo = () => {
        if (original === null) {
            return;
        }
        written.current = null;
        setOriginal(null);
        setValue('description', original, { shouldDirty: true });
    };

    return { isAiText: original !== null, apply, undo };
}
